import React from "react";
import { formatValue, formatPercentage } from "../../components/Utils";
import { SparkLine } from "../chart-components/SparkLine";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGoogle } from "@fortawesome/free-brands-svg-icons";
import dayjs from "dayjs";

// Helper function to parse trend data (can be string or array)
const parseTrendData = (trendData) => {
    if (!trendData) return [];
    if (Array.isArray(trendData)) return trendData;
    if (typeof trendData === 'string') {
        try {
            return JSON.parse(trendData);
        } catch (e) {
            // Invalid JSON string, return empty array
            console.warn('Failed to parse trend data as JSON:', e);
            return [];
        }
    }
    return [];
};

// Calculate average from trend data
const calculateAverage = (trendData) => {
    const data = parseTrendData(trendData);
    if (data.length === 0) return 0;
    const sum = data.reduce((acc, val) => acc + (Number(val) || 0), 0);
    return sum / data.length;
};

// Calculate max from trend data
const calculateMax = (trendData) => {
    const data = parseTrendData(trendData);
    if (data.length === 0) return 0;
    return Math.max(...data.map(val => Number(val) || 0));
};

// Calculate volatility (coefficient of variation) from trend data
const calculateVolatility = (trendData) => {
    const data = parseTrendData(trendData);
    if (data.length === 0) return 0;
    
    const values = data.map(val => Number(val) || 0);
    const mean = values.reduce((acc, val) => acc + val, 0) / values.length;
    
    if (mean === 0) return 0;
    
    const variance = values.reduce((acc, val) => acc + Math.pow(val - mean, 2), 0) / values.length;
    const stdDev = Math.sqrt(variance);
    
    // Coefficient of variation (standard deviation / mean)
    return (stdDev / mean) * 100;
};

// Cost table measures - only available in cost tables
export const COST_TABLE_MEASURES = {
    current_period: {
        id: "current_period",
        label: "Current period",
        headerTooltip: "The total cost",
        minWidth: 125,
        maxWidth: 125,
        cell: (row) => formatValue(row.value, "currency", false),
        className: "text-coolgrey3",
        needsTrendData: false,
    },
    previous_period: {
        id: "previous_period",
        label: "Previous period",
        headerTooltip: "The previous period total",
        minWidth: 125,
        maxWidth: 125,
        cell: (row) => formatValue(row.value, "currency", false),
        className: "text-coolgrey3",
        needsTrendData: false,
    },
    change: {
        id: "change",
        label: "Change vs Previous",
        headerTooltip: "The change in the cost between the current and previous period",
        minWidth: 125,
        maxWidth: 125,
        cell: (row) => formatValue(row.value, "currency", true),
        dynamicClassName: (v) => (v <= 0 ? "text-alert-red" : "text-alert-green"),
        needsTrendData: false,
    },
    change_percentage: {
        id: "change_percentage",
        label: "Change % vs Previous",
        headerTooltip: "The percentage change in the cost between the current and previous period",
        minWidth: 125,
        maxWidth: 125,
        cell: (row) => formatPercentage(row.value),
        dynamicClassName: (v) => (v <= 0 ? "text-alert-red" : "text-alert-green"),
        needsTrendData: false,
    },
    trend: {
        id: "trend",
        label: "Trend",
        headerTooltip: "The cost trend",
        minWidth: 100,
        maxWidth: 100,
        cell: (row) => <SparkLine data={row.value} width={100} showAsArea={true} showAverageLine={true} />,
        className: "text-text font-semibold",
        needsTrendData: true,
    },
    daily_average_cost: {
        id: "daily_average_cost",
        label: "Daily Average",
        headerTooltip: "The daily average cost from the trend data",
        minWidth: 125,
        maxWidth: 125,
        cell: (row) => {
            const trendData = row.row?.original?.trend || row.value;
            const avg = calculateAverage(trendData);
            return formatValue(avg, "currency", false);
        },
        className: "text-text font-semibold",
        needsTrendData: true,
    },
    trend_max_daily_cost: {
        id: "trend_max_daily_cost",
        label: "Maximum per Day",
        headerTooltip: "The maximum daily cost from the trend data",
        minWidth: 125,
        maxWidth: 125,
        cell: (row) => {
            const trendData = row.row?.original?.trend || row.value;
            const max = calculateMax(trendData);
            return formatValue(max, "currency", false);
        },
        className: "text-text font-semibold",
        needsTrendData: true,
    },
    volatility: {
        id: "volatility",
        label: "Volatility",
        headerTooltip: "The volatility (coefficient of variation) of the daily costs. Low = <30%, High = ≥30%",
        minWidth: 125,
        maxWidth: 125,
        accessor: (row) => {
            // Calculate volatility percentage for use in dynamicClassName
            const trendData = row.trend;
            return calculateVolatility(trendData);
        },
        cell: (row) => {
            // row.value will be the volatility percentage from the accessor
            const volatility = row.value;
            // Threshold: 30% coefficient of variation
            const threshold = 30;
            return volatility < threshold ? "Low" : "High";
        },
        dynamicClassName: (v) => {
            // v will be the volatility percentage from the accessor
            const threshold = 30;
            return v < threshold ? "text-alert-green" : "text-alert-red";
        },
        needsTrendData: true,
    },
    percentage_of_total: {
        id: "percentage_of_total",
        label: "% of Total",
        headerTooltip: "The percentage this row represents of the total cost",
        minWidth: 125,
        maxWidth: 125,
        cell: (row) => {
            // row.value will be an object with { currentValue, total } from the custom accessor
            const { currentValue, total } = row.value || { currentValue: 0, total: 0 };
            
            // Calculate percentage
            const percentage = total > 0 ? (currentValue / total) * 100 : 0;
            return formatPercentage(percentage, false);
        },
        className: "text-text font-semibold",
        needsTrendData: false,
    },
};

// Forecast table measures - only available in forecast tables
export const FORECAST_TABLE_MEASURES = {
    previous_month_actual_cost: {
        id: "previous_month_actual_cost",
        label: "Previous Month Actual",
        headerTooltip: "Actual cost in the previous month",
        minWidth: 150,
        maxWidth: 150,
        cell: (row) => formatValue(row.value, "currency", false),
        className: "text-text",
        needsTrendData: false,
    },
    current_month_actual_cost: {
        id: "current_month_actual_cost",
        label: "Current Month Actual",
        headerTooltip: "Actual cost in the current period (without forecast)",
        minWidth: 150,
        maxWidth: 150,
        cell: (row) => formatValue(row.value, "currency", false),
        className: "text-text",
        needsTrendData: false,
    },
    forecasted_cost: {
        id: "forecasted_cost",
        label: "Current Month Forecast",
        headerTooltip: "Sum of actual cost in the current period + forecast cost for the current period",
        minWidth: 150,
        maxWidth: 150,
        cell: (row) => formatValue(row.value, "currency", false),
        className: "text-text font-semibold",
        needsTrendData: false,
    },
    forecast_change: {
        id: "forecast_change",
        label: "€ Change vs Previous",
        headerTooltip: "Forecasted cost of current month (actual + forecast) vs actual cost of previous month",
        minWidth: 180,
        maxWidth: 180,
        cell: (row) => formatValue(row.value, "currency", true),
        dynamicClassName: (v) => (v > 0 ? "text-alert-red" : v < 0 ? "text-alert-green" : "text-text-3"),
        needsTrendData: false,
    },
    forecast_change_percentage: {
        id: "forecast_change_percentage",
        label: "% Change vs Previous",
        headerTooltip: "Forecasted cost of current month (actual + forecast) vs actual cost of previous month",
        minWidth: 180,
        maxWidth: 180,
        cell: (row) => formatPercentage(row.value),
        dynamicClassName: (v) => (v > 0 ? "text-alert-red" : v < 0 ? "text-alert-green" : "text-text-3"),
        needsTrendData: false,
    },
    forecast_trend: {
        id: "forecast_trend",
        label: "Trend",
        headerTooltip: "Trend indicator based on costs (actual + forecasted) in current month: ↑ (red) increasing, ↓ (green) decreasing, → (gray) stable",
        minWidth: 100,
        maxWidth: 100,
        cell: (row) => {
            const change = row.value;
            if (change > 0) {
                return <span className="text-alert-red text-xl font-bold">↑</span>;
            } else if (change < 0) {
                return <span className="text-alert-green text-xl font-bold">↓</span>;
            } else {
                return <span className="text-text-3 text-xl font-bold">→</span>;
            }
        },
        className: "text-center",
        needsTrendData: false,
    },
};

// Insight table measures - only available in insight tables
export const INSIGHT_TABLE_MEASURES = {
    insight: {
        id: "insight",
        label: "Insight",
        headerTooltip: "The insight description",
        minWidth: 300,
        maxWidth: 400,
        cell: (row) => <span className="text-text truncate block">{row.value || '-'}</span>,
        className: "text-left",
        needsTrendData: false,
    },
    insight_group: {
        id: "insight_group",
        label: "Category",
        headerTooltip: "The category of the insight",
        minWidth: 150,
        maxWidth: 200,
        cell: (row) => <span className="text-text">{row.value || '-'}</span>,
        className: "text-left",
        needsTrendData: false,
    },
    vendor_name: {
        id: "vendor_name",
        label: "Vendor",
        headerTooltip: "The cloud vendor",
        minWidth: 80,
        maxWidth: 80,
        cell: (row) => {
            const vendorName = row.value;
            if (!vendorName) return <span>-</span>;
            
            // Simple vendor display - full icons handled in component
            return <span className="text-text">{vendorName}</span>;
        },
        className: "text-center",
        needsTrendData: false,
    },
    resource_cluster: {
        id: "resource_cluster",
        label: "Cluster",
        headerTooltip: "The resource cluster",
        minWidth: 100,
        maxWidth: 150,
        cell: (row) => <span className="text-text">{row.value || '-'}</span>,
        className: "text-left",
        needsTrendData: false,
    },
    resource_name: {
        id: "resource_name",
        label: "Resource",
        headerTooltip: "The resource name",
        minWidth: 150,
        maxWidth: 250,
        cell: (row) => <span className="text-text">{row.value || '-'}</span>,
        className: "text-left",
        needsTrendData: false,
    },
    units: {
        id: "units",
        label: "Units",
        headerTooltip: "The number of units associated with the insight",
        minWidth: 75,
        maxWidth: 100,
        cell: (row) => formatValue(row.value, "decimal", false),
        className: "text-right",
        needsTrendData: false,
    },
    since: {
        id: "since",
        label: "Since",
        headerTooltip: "The date when the insight was created",
        minWidth: 100,
        maxWidth: 120,
        accessor: (row) => row.created_at || row.since || null,
        cell: (row) => {
            if (!row.value) return <span className="text-text">-</span>;
            return <span className="text-text">{dayjs(row.value).format("DD-MM-YYYY")}</span>;
        },
        className: "text-left",
        needsTrendData: false,
    },
    costs: {
        id: "costs",
        label: "Savings",
        headerTooltip: "The total costs/savings associated with the insight",
        minWidth: 120,
        maxWidth: 150,
        cell: (row) => formatValue(row.value, "currency", false),
        className: "text-right text-text font-semibold",
        needsTrendData: false,
    },
};

// Resource changes table measures - only available in resource changes tables
export const RESOURCE_CHANGES_TABLE_MEASURES = {
    change_date: {
        id: "change_date",
        label: "Change date",
        headerTooltip: "The date of the change",
        minWidth: 150,
        maxWidth: 200,
        cell: (row) => {
            return <span className="text-text">{dayjs(row.value).format("dddd, MMMM D")}</span>;
        },
        className: "text-left text-text",
        needsTrendData: false,
    },
    vendor_name: {
        id: "vendor_name",
        label: "Vendor",
        headerTooltip: "The cloud vendor",
        minWidth: 50,
        maxWidth: 50,
        hideHeader: true,
        cell: (row) => {
            return (
                <div className="text-center text-text font-semibold">
                    <FontAwesomeIcon icon={faGoogle} className="text-text-3" />
                </div>
            );
        },
        className: "text-center",
        needsTrendData: false,
    },
    resource_cluster: {
        id: "resource_cluster",
        label: "Resource cluster",
        headerTooltip: "The resource cluster",
        minWidth: 100,
        maxWidth: 150,
        cell: (row) => <span className="text-text">{row.value || '-'}</span>,
        className: "text-left text-text",
        needsTrendData: false,
    },
    min_change: {
        id: "min_change",
        label: "Min change",
        headerTooltip: "Min change",
        minWidth: 100,
        maxWidth: 120,
        cell: (row) => {
            const value = row.value;
            return value !== 0 ? (
                <span className="text-right text-text">{formatValue(value, "decimal", true)}</span>
            ) : null;
        },
        className: "text-right text-text",
        needsTrendData: false,
    },
    plus_change: {
        id: "plus_change",
        label: "Plus change",
        headerTooltip: "Plus change",
        minWidth: 100,
        maxWidth: 120,
        cell: (row) => {
            const value = row.value;
            return value !== 0 ? (
                <span className="text-right text-text">{formatValue(value, "decimal", true)}</span>
            ) : null;
        },
        className: "text-right text-text",
        needsTrendData: false,
    },
    resource_change_total: {
        id: "resource_change_total",
        label: "Change",
        headerTooltip: "The total change (sum of min change and plus change)",
        minWidth: 100,
        maxWidth: 120,
        accessor: (row) => {
            // Calculate change as sum of min_change and plus_change
            const minChange = row.min_change || 0;
            const plusChange = row.plus_change || 0;
            return minChange + plusChange;
        },
        cell: (row) => {
            const value = row.value;
            return value !== 0 ? (
                <span className={`text-right ${value > 0 ? "text-alert-green" : value < 0 ? "text-alert-red" : "text-text"}`}>
                    {formatValue(value, "decimal", true)}
                </span>
            ) : null;
        },
        className: "text-right",
        dynamicClassName: (v) => {
            if (v > 0) return "text-alert-green";
            if (v < 0) return "text-alert-red";
            return "text-text";
        },
        needsTrendData: false,
    },
    resource_type: {
        id: "resource_type",
        label: "Type",
        headerTooltip: "The type of the resource",
        minWidth: 200,
        maxWidth: 250,
        cell: (row) => <span className="text-text">{row.value || '-'}</span>,
        className: "text-left text-text",
        needsTrendData: false,
    },
    // resource_name: {
    //     id: "resource_name",
    //     label: "Resource",
    //     headerTooltip: "The name of the resource",
    //     minWidth: 250,
    //     maxWidth: 300,
    //     cell: (row) => <span className="text-text">{row.value || '-'}</span>,
    //     className: "text-left text-text",
    //     needsTrendData: false,
    // },
    resource_status: {
        id: "resource_status",
        label: "Current status",
        headerTooltip: "Is the resource currently active or inactive",
        minWidth: 100,
        maxWidth: 150,
        cell: (row) => {
            const value = row.value || '';
            const status = value.toLowerCase();
            const statusClass = status === "active"
                ? "bg-success-soft text-success border border-success"
                : status === "inactive"
                    ? "bg-danger-soft text-danger border border-danger"
                    : "bg-surface-2 text-text-2 border border-border";
            return (
                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${statusClass}`}>
                    {value}
                </span>
            );
        },
        className: "text-left",
        needsTrendData: false,
    },
    cost: {
        id: "cost",
        label: "Cost",
        headerTooltip: "The cost of the resource during the selected time period",
        minWidth: 125,
        maxWidth: 150,
        cell: (row) => formatValue(row.value, "currency", false),
        className: "text-right text-text font-semibold",
        needsTrendData: false,
    },
};

// Resource list table measures - for ResourceListWidget
export const RESOURCE_LIST_TABLE_MEASURES = {
    vendor: {
        id: "vendor",
        label: "Vendor",
        headerTooltip: "The vendor of the resource",
        minWidth: 80,
        maxWidth: 80,
        accessor: "vendor", // Explicitly set accessor to match data field
        cell: (row) => {
            // For group rows (isGroup), don't show vendor
            if (row.original?.isGroup || !row.value) return <div />;
            return (
                <div className="text-center text-text font-semibold pt-1">
                    <FontAwesomeIcon icon={faGoogle} className="text-text-3" />
                </div>
            );
        },
        className: "text-center",
        needsTrendData: false,
    },
    type: {
        id: "type",
        label: "Type",
        headerTooltip: "The type of the resource",
        minWidth: 120,
        maxWidth: 200,
        accessor: "type", // Explicitly set accessor to match data field
        cell: (row) => {
            // For group rows (isGroup), don't show type
            if (row.original?.isGroup) return <div />;
            return <span className="text-text">{row.value || ''}</span>;
        },
        className: "text-left",
        needsTrendData: false,
    },
    units: {
        id: "units",
        label: "Units",
        headerTooltip: "The number of units of the resource",
        minWidth: 100,
        maxWidth: 120,
        accessor: "units", // Explicitly set accessor to match data field
        cell: (row) => {
            const valueClass = row.original?.isGroup 
                ? "text-right text-text font-semibold pt-1"
                : "text-right text-text pt-1";
            return (
                <div className={valueClass}>
                    {formatValue(row.value || 0, "decimal", false)}
                </div>
            );
        },
        className: "text-right",
        needsTrendData: false,
    },
    status: {
        id: "status",
        label: "Status",
        headerTooltip: "Is the resource currently active or inactive",
        minWidth: 100,
        maxWidth: 100,
        accessor: "status", // Explicitly set accessor to match data field
        cell: (row) => {
            // For group rows (isGroup), don't show status
            if (row.original?.isGroup || !row.value) return <div />;
            const status = row.value || '';
            let statusClass = "bg-surface-2 text-text-2 border border-border";
            if (status.toLowerCase() === "active") {
                statusClass = "bg-success-soft text-success border border-success";
            } else if (status.toLowerCase() === "inactive") {
                statusClass = "bg-danger-soft text-danger border border-danger";
            }
            return (
                <div className="text-center pt-1">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${statusClass}`}>
                        {status}
                    </span>
                </div>
            );
        },
        className: "text-center",
        needsTrendData: false,
    },
    cost: {
        id: "cost",
        label: "Cost",
        headerTooltip: "The cost of the resource during the selected time period",
        minWidth: 140,
        maxWidth: 160,
        accessor: "cost", // Explicitly set accessor to match data field
        cell: (row) => {
            if (row.original?.costs_not_known) return <div />;
            const valueClass = row.original?.isGroup 
                ? "text-right text-text font-semibold pt-1"
                : "text-right text-text pt-1";
            return (
                <div className={valueClass}>
                    {formatValue(row.value || 0, "currency", false)}
                </div>
            );
        },
        className: "text-right",
        needsTrendData: false,
    },
    cpu: {
        id: "cpu",
        label: "CPU",
        headerTooltip: "CPU utilization percentage",
        minWidth: 80,
        maxWidth: 100,
        accessor: "cpu", // Explicitly set accessor to match data field (utilization_cpu mapped to cpu)
        cell: (row) => {
            // For group rows (isGroup), don't show CPU
            if (row.original?.isGroup || !row.original?.has_cpu_metrics) return <div />;
            return <span className="text-text">{formatValue((row.value || 0) * 100, "decimal", false, 1)}%</span>;
        },
        className: "text-center",
        needsTrendData: false,
    },
    memory: {
        id: "memory",
        label: "Memory",
        headerTooltip: "Memory utilization percentage",
        minWidth: 90,
        maxWidth: 110,
        accessor: "memory", // Explicitly set accessor to match data field (utilization_memory mapped to memory)
        cell: (row) => {
            // For group rows (isGroup), don't show memory
            if (row.original?.isGroup || !row.original?.has_memory_metrics) return <div />;
            return <span className="text-text">{formatValue((row.value || 0) * 100, "decimal", false, 1)}%</span>;
        },
        className: "text-center",
        needsTrendData: false,
    },
    storage: {
        id: "storage",
        label: "Storage",
        headerTooltip: "Storage utilization percentage",
        minWidth: 90,
        maxWidth: 110,
        accessor: "storage", // Explicitly set accessor to match data field (utilization_storage mapped to storage)
        cell: (row) => {
            // For group rows (isGroup), don't show storage
            if (row.original?.isGroup || !row.original?.has_storage_metrics) return <div />;
            return <span className="text-text">{formatValue((row.value || 0) * 100, "decimal", false, 1)}%</span>;
        },
        className: "text-center",
        needsTrendData: false,
    },
};

// Legacy export for backward compatibility (deprecated - use specific measure registries instead)
export const TABLE_MEASURES = {
    ...COST_TABLE_MEASURES,
    ...FORECAST_TABLE_MEASURES,
    ...RESOURCE_CHANGES_TABLE_MEASURES,
    ...INSIGHT_TABLE_MEASURES,
    ...RESOURCE_LIST_TABLE_MEASURES,
};

export const DEFAULT_TABLE_MEASURES = ["change", "change_percentage", "current_period"];
export const DEFAULT_FORECAST_TABLE_MEASURES = ["previous_month_actual_cost", "current_month_actual_cost", "forecasted_cost", "forecast_trend"];
export const DEFAULT_RESOURCE_CHANGES_TABLE_MEASURES = ["vendor_name", "change_date", "resource_change_total"];
export const DEFAULT_INSIGHT_TABLE_MEASURES = ["insight", "insight_group", "resource_name", "costs"];
export const DEFAULT_RESOURCE_LIST_TABLE_MEASURES = ["units", "cpu", "memory", "storage", "vendor"];


